import { AuthenticatedRequestHandler, IAuthenticatedRequestHandlerResponse } from '@api/utils/AuthenticatedRequestHandler'
import AtCoreClient from 'IhmAssociateToolsCoreService-coral-javascript-clients/jquery-client'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { TokenUtils } from '@utils/TokenUtils'

export class AtCoreServiceClient {
    private static instance: AtCoreServiceClient
    private readonly client: any
    private readonly endpoint: string

    public static getInstance (): AtCoreServiceClient {
        if (!AtCoreServiceClient.instance) {
            AtCoreServiceClient.instance = new AtCoreServiceClient()
        }
        return AtCoreServiceClient.instance
    }

    /**
     * Assumes an IAM Role and returns a set of AWS Auth credentials.
     *
     * @param {string} storeId
     * @param {string} roleName
     * @return {Promise} AWS Auth credentials. For example:
     * {
     *      "accessKeyId": "{string}",
     *      "expirationTimestamp": {number} - in milliseconds,
     *      "secretAccessKey": "{string}",
     *      "sessionToken": {string}
     * }
     */
    public async assumeUserRole (storeId: string, roleName: string): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.endpoint + 'AssumeUserRole/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                roleName,
            }})
    }

    /**
     * Returns store info from Libby.
     *
     * @param {string} storeId
     * @return {Promise} Store info. For example.
     * {
     *     "storeFamily": "IHM",
     *     "primePricingEnabled": false,
     *     "tags": [
     *       "CAP_ESL",
     *       "CAP_JWO_ENTRY_EXIT",
     *       "CAP_JWO_CAMERA",
     *       "REAL",
     *       "StoneRetail",
     *       "CAP_JWO_LOAD_SENSOR",
     *       "CAP_JWO_RE_AUTH",
     *       "RESTRICTEDACCESS",
     *       "IHM"
     *     ],
     *     "launchDate": "2013-01-01T00:00:00-07:00",
     *     "pickupAddressId": "FYVMTT5FZ4VPMQ27JHXUG12ECGO6YPPUMA1TMUPPY6OGCE4PXTQ2EIA2OXKRXPIW"
     * }
     */
    public async getStoreInfo (storeId: string): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.endpoint + 'GetStoreInfo/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
            }})
    }

    /**
     * Returns the treatment assignment for the given store Id and AppConfig details.
     *
     * @param {string} storeId
     * @param {string} applicationName: This is the application name for App config.
     * @param {string} environment: The environment of App config we want to use.
     * @param {string} clientId: The client Id to be used to assume credentials of IAM role
     *                 of account where APP config exists.
     * @param {string} configurationName: The name of the config file.
     * eg: IHM_AX_180487
     *
     * @returns {map} if the feature is enabled or not.
     * eg. {"treatmentAssignment": {"featureEnabled": "true"}}
     */
    public async getTreatmentAssignment (storeId, applicationName, environment, clientId,
                                         configurationName): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.replaceSINEndpointWithPDXIfNeeded(this.endpoint) + 'GetTreatmentAssignment/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                applicationName,
                environment,
                clientId,
                configurationName,
            }})
    }

    /**
     * Returns user Alias.
     * In case of IIB Authentication, the user alias will be fetched from IIB token
     * it will return a promise which directly resolves to the user alias.
     *
     * In case of non-IIB Authentication it will call ATCore GetUserAlias API and returns
     * Promise<IAuthenticatedRequestHandlerResponse>. The user alias will be a field in the API response body.
     *
     * @param {string} storeId
     * @return {Promise} user alias. For example
     */
    public async getUserAlias (storeId: string): Promise<any> {
        // Mock user alias until the actual implementation is in place.
        console.log('returning mock user alias')
        return Promise.resolve('dev-user')
    }

    /**
     * Returns encrypted token.
     * The API is only supported for IIB Authentication.
     *
     * It will call ATCore GetEncryptedToken API and returns
     * Promise<IAuthenticatedRequestHandlerResponse>. The encrypted token will be a field in the API response body.
     *
     * @param {string} storeId
     * @return {Promise} encryptedToken. For example
     * {
     *     "encryptedToken" : "abcdef"
     * }
     */
    public async getEncryptedToken (storeId: string): Promise<any> {
        if (EnvironmentDetail.isIIBAuthN()) {
            return this.sendRequest({
                url: this.endpoint + 'GetEncryptedToken/' + storeId,
                method: 'GET',
                queryParams: {
                    storeId,
                },
            })
        }
        throw Error('API not supported if not IIB authenticated')
    }

    /**
     * Returns Zone config for a store.
     *
     * @param {string} storeId
     * @return {Promise} zone config.
     */
    public async getZoneConfigs (storeId: string): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.endpoint + 'GetZoneConfigs/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
            }})
    }

    /**
     * Fetches the menu and the tool registrations
     * TODO: Remove kelpie specific parameter once path forward is figured out for kelpie
     * @param storeId
     * @param kelpieFeatureNameToIsEnabledStatus - This says if a particular kelpie feature is enabled or not
     */
    public async getMenu (storeId: string, kelpieFeatureNameToIsEnabledStatus): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.endpoint + 'GetMenu/' + storeId,
            method: 'GET',
            skipIdTokenInUrl: true,
            queryParams: {
                storeId,
                kelpieFeatureNameToIsEnabledStatus,
            }})
    }

    /**
     * Retrieves ALM store extensions for a specific store.
     *
     * @param storeId - The unique identifier of the store.
     * @param extensions - An array of extension names to retrieve.
     * @return {Promise} - Extensions.
     */
    public async getALMStoreExtensions (storeId: string, extensions: string[]): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'GetALMStoreExtensions/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                extensions,
            },
        })
    }

    /**
     * Returns User context for a user at a store.
     *
     * @param {string} storeId
     * @param {string} userId
     * @return {Promise} user context.
     */
    public async getUserContext (storeId: string, userId: string): Promise<any> {
        console.log('calling getUserContext Promise')
        return this.sendRequest({
            url: this.endpoint + 'GetUserContext/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                userId,
            },
        })
    }

    /**
     * Private Constructor setting the AtCoreService client and its endpoint
     * @private
     */
    private constructor () {
        this.client = AtCoreClient
        this.client.config.overrideDomain(EnvironmentDetail.getDomain())
        this.client.config.overrideRealm(EnvironmentDetail.getRealm())
        this.endpoint = this.client.config.endpoint()
        console.log(this.endpoint)
    }

    /**
     * Makes the call to the abstract authenticatedRequestsClient.
     * @param options call parameters.
     * @returns {*} Output from the call (from the underlying service)
     */
    private async sendRequest (options): Promise<IAuthenticatedRequestHandlerResponse> {
        const queryParamString = this.buildQueryParamString(options.queryParams)
        options.url = options.url + queryParamString + '&toolName=AssociateToolBase'
        delete options.queryParams
        return AuthenticatedRequestHandler.raw(options)
    }

    /**
     * Converts queryParam json object to queryParam string.
     * Handles both single values and arrays.
     *
     * @param queryParams Object containing query parameters.
     * @return {string} Formatted query string.
     */
    private buildQueryParamString (queryParams: Record<string, string | string[]>): string {
        const queryParts: string[] = []

        Object.keys(queryParams).forEach((key) => {
            const value = queryParams[key]
            if (Array.isArray(value)) {
                // Handle array values
                value.forEach((item) => {
                    queryParts.push(`${key}=${item}`)
                })
            } else {
                // Handle single values
                queryParts.push(`${key}=${value}`)
            }
        })

        return queryParts.length ? `?${queryParts.join('&')}` : ''
    }

    /**
     * Replaces the SIN endpoint for ATCore API Client with the corresponding PDX endpoint.
     * For other regions it won't change the endpoint. This is done because weblab API support isn't there for SIN
     * Eg input -  https://api.ap-southeast-1.gamma.atcoreservice.ihm.amazon.dev
     * Corresponding output -  https://api.us-west-2.gamma.atcoreservice.ihm.amazon.dev
     * Reference tt: https://t.corp.amazon.com/D86199797/information
     * @param endpoint
     * @private
     */
    private replaceSINEndpointWithPDXIfNeeded (endpoint: string): string {
        console.log('replaceSINEndpointWithPDXIfNeeded called for region: ', EnvironmentDetail.getRealm())
        console.log('replaceSINEndpointWithPDXIfNeeded returns:',
            endpoint.replace('ap-southeast-1', 'us-west-2'))
        return endpoint.replace('ap-southeast-1', 'us-west-2')
    }
}
